import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDownRegular, WarningRegular, PersonRegular, CalendarRegular, DocumentRegular, BuildingRegular, CheckmarkRegular, FlagRegular, OpenRegular, HomeRegular, TextAlignJustifyRegular, SearchRegular } from '@fluentui/react-icons';
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';
import incidentService from '../services/incidentService';
import processService from '../services/processService';
import incidentCommunicationsService from '../services/incidentCommunicationsService';
import config from '../../config';

export default function IncidentInfo({ isOpen, onToggle, contextVersion }) {
  const { t } = useLanguage();
  const { currentTextSize } = useTextSize();
  const [incidentData, setIncidentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Process assignment state
  const [showProcessAssignment, setShowProcessAssignment] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [instructions, setInstructions] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  useEffect(() => {
    const fetchIncidentData = async () => {
      // Only fetch if section is open and we have an incident ID
      if (!isOpen || !incidentService.hasIncidentId()) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await incidentService.getIncident();
        if (result.success) {
          setIncidentData(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch incident data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchIncidentData();
  }, [isOpen, contextVersion]);

  // Fetch processes when showing process assignment
  useEffect(() => {
    const fetchProcesses = async () => {
      if (!showProcessAssignment) return;
      
      setLoadingProcesses(true);
      try {
        const result = await processService.getAllProcesses();
        if (result.success && result.data) {
          setProcesses(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch processes:', err);
      } finally {
        setLoadingProcesses(false);
      }
    };

    fetchProcesses();
  }, [showProcessAssignment]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Filter and sort processes based on search query
  const filteredProcesses = useMemo(() => {
    if (!processes || !Array.isArray(processes)) return [];
    
    const filtered = processes.filter(p => 
      String(p.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return filtered.sort((a, b) => 
      String(a.name || '').localeCompare(String(b.name || ''), 'de', { sensitivity: 'base' })
    );
  }, [processes, searchQuery]);

  // Get selected process name
  const selectedProcessName = useMemo(() => {
    if (!selectedProcessId || !processes) return '';
    const found = processes.find(p => String(p.id) === String(selectedProcessId));
    return found ? found.name : '';
  }, [selectedProcessId, processes]);

  // Handle incident initialization
  const handleInitializeIncident = useCallback(async (withAI = false) => {
    if (!selectedProcessId || !incidentData?.id) return;
    
    try {
      setIsInitializing(true);
      
      const payload = {
        process_id: String(selectedProcessId),
        ai_execution: withAI
      };
      
      // Add instructions if provided
      const trimmedInstructions = instructions.trim();
      if (trimmedInstructions.length > 0) {
        payload.message = trimmedInstructions;
      }
      
      // Add thread_id if available
      const threadId = incidentCommunicationsService.getThreadId();
      if (threadId) {
        payload.thread_id = threadId;
      }
      
      const result = await processService.initializeIncident(incidentData.id, payload);
      
      if (result.success) {
        // Open incident detail page in DoNexus
        window.open(`${config.APP_BASE_URL}/incidents/${incidentData.id}`, '_blank');
        
        // Refresh incident data
        const refreshedIncident = await incidentService.getIncident();
        if (refreshedIncident.success) {
          setIncidentData(refreshedIncident.data);
        }
        
        // Reset state
        setShowProcessAssignment(false);
        setSelectedProcessId(null);
        setInstructions('');
        setSearchQuery('');
        setHasUserSelected(false);
      }
    } catch (err) {
      console.error('Failed to initialize incident:', err);
      setError(t('incidentInfo.initializeError') + ': ' + err.message);
    } finally {
      setIsInitializing(false);
    }
  }, [selectedProcessId, incidentData, instructions, t]);

  const hasInstructions = instructions.trim().length > 0;

  const getPriorityColor = (priority) => {
    const priorityLower = priority?.toLowerCase();
    if (priorityLower === 'high' || priorityLower === 'hoch') {
      return 'bg-orange-100 text-orange-800 border-orange-200';
    } else if (priorityLower === 'medium' || priorityLower === 'mittel') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (priorityLower === 'low' || priorityLower === 'niedrig') {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'completed' || statusLower === 'abgeschlossen') {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (statusLower === 'in_progress' || statusLower === 'in progress' || statusLower === 'in bearbeitung') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (statusLower === 'pending' || statusLower === 'ausstehend') {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'pending') return t('incidentInfo.pending');
    if (statusLower === 'in_progress' || statusLower === 'in progress') return t('incidentInfo.inProgress');
    if (statusLower === 'completed') return t('incidentInfo.completed');
    return status;
  };

  const getPriorityLabel = (priority) => {
    const priorityLower = priority?.toLowerCase();
    if (priorityLower === 'high') return t('incidentInfo.high');
    if (priorityLower === 'medium') return t('incidentInfo.medium');
    if (priorityLower === 'low') return t('incidentInfo.low');
    return priority;
  };

  const getPriorityTextColor = (priority) => {
    const priorityLower = priority?.toLowerCase();
    if (priorityLower === 'high' || priorityLower === 'hoch') {
      return 'text-red-600';
    } else if (priorityLower === 'medium' || priorityLower === 'mittel') {
      return 'text-yellow-600';
    } else if (priorityLower === 'low' || priorityLower === 'niedrig') {
      return 'text-green-600';
    }
    return 'text-gray-600';
  };
  
  return (
    <div className="border-b border-gray-300">
      <button 
        className={`flex items-center justify-between w-full ${currentTextSize.cardPadding} hover:bg-gray-50 transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <DocumentRegular className="h-4 w-4 text-gray-600" />
          <h3 className={`${currentTextSize.headerText} font-semibold text-gray-900`}>{t('incidentInfo.title')}</h3>
        </div>
        <ChevronDownRegular
          className={`h-4 w-4 text-gray-500 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className={`px-4 pb-4 ${currentTextSize.spacing}`}>
          {loading && (
            <div className="text-center py-4">
              <p className={`${currentTextSize.bodyText} text-gray-500`}>
                {t('loading')}
              </p>
            </div>
          )}

          {error && (
            <div className="text-center py-4">
              <p className={`${currentTextSize.bodyText} text-red-600`}>
                {t('incidentInfo.error')}: {error}
              </p>
            </div>
          )}

          {!loading && !error && !incidentService.hasIncidentId() && (
            <div className="text-center py-4">
              <p className={`${currentTextSize.bodyText} text-gray-500`}>
                {t('incidentInfo.noIncident')}
              </p>
            </div>
          )}

          {!loading && !error && incidentData && incidentData.initialized === false && (
            <div className="space-y-3">
              {/* Not Initialized Message */}
              <div className="text-center py-3">
                <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 mb-1`}>
                  {t('incidentInfo.notInitialized')}
                </h4>
                <p className={`${currentTextSize.bodyText} text-gray-500`}>
                  {t('incidentInfo.notInitializedMessage')}
                </p>
              </div>

              {/* Create New Incident Button */}
              {!showProcessAssignment && (
                <button 
                  className={`w-full flex items-center justify-center text-white bg-blue-600 hover:bg-blue-700 ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium transition-colors`}
                  onClick={() => setShowProcessAssignment(true)}
                >
                  <DocumentRegular className="h-4 w-4 mr-2" />
                  {t('incidentInfo.createNewIncident')}
                </button>
              )}

              {/* Process Assignment UI */}
              {showProcessAssignment && (
                <div className="space-y-3 border border-gray-200 rounded-lg p-3">
                  {/* Search Input */}
                  <div className="relative">
                    <SearchRegular className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('incidentInfo.searchProcesses')}
                      className={`w-full pl-9 pr-3 py-2 border border-gray-300 rounded ${currentTextSize.bodyText} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                  </div>

                  {/* Process List */}
                  {loadingProcesses ? (
                    <div className="text-center py-4">
                      <p className={`${currentTextSize.bodyText} text-gray-500`}>
                        {t('incidentInfo.loadingProcesses')}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded">
                      <div className="p-2">
                        <p className={`${currentTextSize.bodyText} text-gray-500 mb-2 px-2`}>
                          {t('incidentInfo.processes')}
                        </p>
                        {filteredProcesses.length === 0 ? (
                          <p className={`${currentTextSize.bodyText} text-gray-500 text-center py-4`}>
                            {t('incidentInfo.noProcessesFound')}
                          </p>
                        ) : (
                          filteredProcesses.map((process) => {
                            const isSelected = selectedProcessId && String(process.id) === String(selectedProcessId);
                            return (
                              <button
                                key={process.id}
                                onClick={() => {
                                  setSelectedProcessId(process.id);
                                  setHasUserSelected(true);
                                }}
                                className={`w-full text-left px-3 py-2 rounded mb-1 transition-colors ${
                                  isSelected 
                                    ? 'bg-blue-50 border border-blue-200' 
                                    : 'hover:bg-gray-50 border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className={`${currentTextSize.bodyText} text-gray-900`}>
                                    {process.name}
                                  </span>
                                  {isSelected && (
                                    <span className={`${currentTextSize.bodyText} text-blue-600 text-xs`}>
                                      {t('incidentInfo.selected')}
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <button
                    onClick={() => handleInitializeIncident(false)}
                    disabled={!selectedProcessId || isInitializing}
                    className={`w-full flex items-center justify-center text-white ${
                      !selectedProcessId || isInitializing 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    } ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium transition-colors`}
                  >
                    {isInitializing ? t('incidentInfo.initializing') : t('incidentInfo.makeIncident')}
                  </button>

                  {/* Instructions Textarea */}
                  <div>
                    <textarea
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder={t('incidentInfo.instructionsPlaceholder')}
                      className={`w-full px-3 py-2 border border-gray-300 rounded ${currentTextSize.bodyText} focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]`}
                      rows={3}
                    />
                  </div>

                  {/* Process with AI Button */}
                  <button
                    onClick={() => handleInitializeIncident(true)}
                    disabled={!selectedProcessId || isInitializing || !hasInstructions}
                    className={`w-full flex items-center justify-center ${
                      !selectedProcessId || isInitializing || !hasInstructions
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-300' 
                        : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                    } ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium transition-all`}
                  >
                    {isInitializing ? t('incidentInfo.initializing') : t('incidentInfo.processWithAI')}
                  </button>

                  {/* Cancel Button */}
                  <button
                    onClick={() => {
                      setShowProcessAssignment(false);
                      setSelectedProcessId(null);
                      setInstructions('');
                      setSearchQuery('');
                      setHasUserSelected(false);
                    }}
                    disabled={isInitializing}
                    className={`w-full ${currentTextSize.padding} ${currentTextSize.bodyText} text-gray-600 hover:text-gray-800 transition-colors`}
                  >
                    {t('contactCreator.cancel')}
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && !error && incidentData && incidentData.initialized !== false && (
            <>
              {/* Header with Title and Badges */}
              <div className="flex items-start gap-3 mb-3">
                <WarningRegular className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h4 className={`${currentTextSize.headerText} font-semibold text-gray-900 break-words`}>
                    {incidentData.description || t('incidentInfo.untitled')}
                  </h4>
                  <p className={`${currentTextSize.bodyText} text-gray-500 mt-1`}>
                    {incidentData.token || incidentData.id?.slice(0, 13) || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              {incidentData.status && (
                <div className="mb-3">
                  <span className={`${getStatusColor(incidentData.status)} ${currentTextSize.buttonText} px-3 py-1 rounded-full border font-medium`}>
                    {getStatusLabel(incidentData.status)}
                  </span>
                </div>
              )}

              {/* Incident Details */}
              <div className="space-y-2 mb-4">
                {incidentData.responsible_name && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <PersonRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.assignedTo')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">{incidentData.responsible_name}</span>
                    </div>
                  </div>
                )}
                <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                  <CalendarRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-600">{t('incidentInfo.created')}</span>
                    <span className="text-gray-900 font-medium ml-1">{formatDate(incidentData.created_at)}</span>
                  </div>
                </div>

                {incidentData.process?.name && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <DocumentRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.process')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">{incidentData.process.name}</span>
                    </div>
                  </div>
                )}

                {incidentData.property_name && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <BuildingRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.property')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">{incidentData.property_name}</span>
                    </div>
                  </div>
                )}

                {incidentData.object_name && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <HomeRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.object')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">
                        {incidentData.object_name}
                      </span>
                    </div>
                  </div>
                )}

                {incidentData.unit_name && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <HomeRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.unit')}</span>
                      <span className="text-gray-900 font-medium ml-1 break-words">
                        {incidentData.unit_name}
                      </span>
                    </div>
                  </div>
                )}

                {incidentData.priority && (
                  <div className={`flex items-start gap-2 ${currentTextSize.bodyText}`}>
                    <FlagRegular className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-gray-600">{t('incidentInfo.priority')}</span>
                      <span className={`font-medium ml-1 break-words ${getPriorityTextColor(incidentData.priority)}`}>
                        {getPriorityLabel(incidentData.priority)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Summary Section (Collapsable) */}
              {incidentData.summary && (
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    className={`w-full flex items-center justify-between ${currentTextSize.padding} bg-gray-50 hover:bg-gray-100 transition-colors`}
                    onClick={() => setSummaryExpanded(!summaryExpanded)}
                  >
                    <div className="flex items-center gap-2">
                      <TextAlignJustifyRegular className="h-4 w-4 text-gray-600" />
                      <span className={`${currentTextSize.bodyText} font-medium text-gray-900`}>
                        {t('incidentInfo.summary')}
                      </span>
                    </div>
                    <ChevronDownRegular
                      className={`h-4 w-4 text-gray-500 transition-transform ${
                        summaryExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {summaryExpanded && (
                    <div className={`${currentTextSize.padding} bg-white`}>
                      <p className={`${currentTextSize.bodyText} text-gray-700 leading-relaxed`}>
                        {incidentData.summary}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* View in DoNexus Button */}
              <button 
                className={`w-full flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 ${currentTextSize.padding} rounded ${currentTextSize.bodyText} font-medium transition-colors border border-blue-200`}
                onClick={() => {
                  window.open(`${config.APP_BASE_URL}/incidents/${incidentData.id}`, '_blank');
                }}
              >
                <OpenRegular className="h-4 w-4 mr-2" />
                {t('incidentInfo.viewIncidentInDoNexus')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
